import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  fetchCompetitionGame,
  fetchMyBetsInCompetition,
  postOrUpdateBet
} from '../services/competitionApi';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { BetInput } from '../components/dashboard/BetInput';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Calendar, Target, Users, Eye, PencilLine } from 'lucide-react';
import dayjs from 'dayjs';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { ScoreText } from '../components/ui/ScoreText';

const PlaceBet = () => {
  const { gameId, competitionSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const basePath = `/c/${competitionSlug}`;

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [betData, setBetData] = useState({ homeScore: '', awayScore: '' });
  const [isEditingExistingBet, setIsEditingExistingBet] = useState(false);
  const [otherUsersBets, setOtherUsersBets] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (gameId && competitionSlug) fetchGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, competitionSlug]);

  const fetchGame = async () => {
    try {
      const gameData = await fetchCompetitionGame(competitionSlug, gameId);
      setGame(gameData);

      try {
        const myBets = await fetchMyBetsInCompetition(competitionSlug);
        const userBets = myBets.filter((bet) => {
          const gid = bet.gameId?._id || bet.gameId;
          return String(gid) === String(gameId);
        });

        if (userBets.length > 0) {
          const existingBet = userBets[0];
          setIsEditingExistingBet(true);
          if (
            typeof existingBet.predictedResult === 'object' &&
            existingBet.predictedResult.homeScore !== undefined
          ) {
            setBetData({
              homeScore: existingBet.predictedResult.homeScore.toString(),
              awayScore: existingBet.predictedResult.awayScore.toString()
            });
          } else if (
            typeof existingBet.predictedResult === 'string' &&
            existingBet.predictedResult.includes(':')
          ) {
            const [homeScore, awayScore] = existingBet.predictedResult.split(':');
            setBetData({ homeScore, awayScore });
          }
        }
      } catch {
        /* optional pre-fill */
      }

      try {
        const otherBetsResponse = await axios.get(`/bets/game/${gameId}/users`);
        const betsData = otherBetsResponse.data.bets || otherBetsResponse.data || [];
        setOtherUsersBets(
          betsData
            .filter((bet) => bet.userId !== user._id)
            .map((bet) => ({
              ...bet,
              user: {
                _id: bet.userId,
                fullName: bet.username || bet.user?.fullName || 'Unknown User',
                username: bet.user?.username || bet.username || 'Unknown'
              }
            }))
        );
      } catch {
        setOtherUsersBets([]);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
      toast.error(language === 'en' ? 'Failed to load game' : 'נכשל בטעינת המשחק');
      navigate(`${basePath}/upcoming-games`);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBet = async () => {
    if (!betData.homeScore || !betData.awayScore) {
      toast.error(language === 'en' ? 'Please enter both scores' : 'אנא הזן את שתי התוצאות');
      return;
    }

    try {
      setSubmitting(true);
      await postOrUpdateBet(competitionSlug, {
        gameId: game._id,
        predictedResult: {
          homeScore: parseInt(betData.homeScore, 10),
          awayScore: parseInt(betData.awayScore, 10)
        }
      });
      toast.success(language === 'en' ? 'Bet saved successfully!' : 'ההימור נשמר בהצלחה!');
      navigate(`${basePath}/upcoming-games`);
    } catch (error) {
      console.error('Error placing bet:', error);
      toast.error(
        error.response?.data?.message ||
          (language === 'en' ? 'Failed to place bet' : 'נכשל בהצבת ההימור')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLoading message={language === 'en' ? 'Loading game…' : 'טוען משחק…'} />
    );
  }

  if (!game) {
    return (
      <PageContainer>
        <Card className="py-12 text-center">
          <p className="font-semibold text-zinc-800">
            {language === 'en' ? 'Game not found' : 'המשחק לא נמצא'}
          </p>
          <Button className="mt-4" onClick={() => navigate(`${basePath}/upcoming-games`)}>
            {language === 'en' ? 'Back to games' : 'חזור למשחקים'}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const homeLabel =
    language === 'en' ? t('homeScore') || 'Home' : isRTL ? 'בית (ב)' : 'בית';
  const awayLabel =
    language === 'en' ? t('awayScore') || 'Away' : isRTL ? 'חוץ (ח)' : 'חוץ';

  return (
    <PageContainer>
      <PageHeader
        title={
          isEditingExistingBet
            ? language === 'en'
              ? 'Edit your bet'
              : 'ערוך את ההימור שלך'
            : t('placeBet') || (language === 'en' ? 'Place bet' : 'הצב הימור')
        }
        subtitle={
          isEditingExistingBet
            ? language === 'en'
              ? 'Update your prediction'
              : 'עדכן את הניבוי שלך'
            : language === 'en'
              ? 'Make your prediction'
              : 'בצע את הניבוי שלך'
        }
        backTo={`${basePath}/upcoming-games`}
        icon={Target}
      />

      <div className="mx-auto max-w-lg space-y-6">
        <Card className="text-center">
          <div className="mb-4 flex flex-wrap items-center justify-center gap-3">
            <div className="flex items-center gap-2">
              <img
                src={game.homeGroupId?.logoUrl || '/default-team.png'}
                alt=""
                className="h-10 w-10 rounded-full object-cover ring-2 ring-zinc-100"
              />
              <span className="font-semibold text-zinc-900">
                {getTeamDisplayName(game.homeGroupId, language)}
              </span>
            </div>
            <span className="text-sm font-bold uppercase tracking-wide text-zinc-400">
              {t('vs')}
            </span>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-zinc-900">
                {getTeamDisplayName(game.awayGroupId, language)}
              </span>
              <img
                src={game.awayGroupId?.logoUrl || '/default-team.png'}
                alt=""
                className="h-10 w-10 rounded-full object-cover ring-2 ring-zinc-100"
              />
            </div>
          </div>
          <p className="inline-flex items-center gap-2 text-sm text-zinc-600">
            <Calendar className="h-4 w-4 text-brand-600" />
            {dayjs(`${game.date} ${game.hour}`).format('DD/MM/YYYY HH:mm')}
          </p>
          {game.round ? (
            <span className="mt-3 inline-block rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-800">
              {game.round}
            </span>
          ) : null}
        </Card>

        {isEditingExistingBet ? (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <PencilLine className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">
                {language === 'en' ? 'Editing existing bet' : 'עריכת הימור קיים'}
              </p>
              <p className="mt-0.5 text-amber-800/90">
                {language === 'en'
                  ? 'You can change your prediction until kickoff.'
                  : 'ניתן לשנות את הניבוי עד תחילת המשחק.'}
              </p>
            </div>
          </div>
        ) : null}

        <Card>
          <BetInput
            idPrefix={`bet-${gameId}`}
            homeValue={betData.homeScore}
            awayValue={betData.awayScore}
            onHomeChange={(v) => setBetData({ ...betData, homeScore: v })}
            onAwayChange={(v) => setBetData({ ...betData, awayScore: v })}
            homeLabel={homeLabel}
            awayLabel={awayLabel}
            isRTL={isRTL}
          />
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => navigate(`${basePath}/upcoming-games`)}
            >
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              variant={isEditingExistingBet ? 'success' : 'primary'}
              loading={submitting}
              onClick={handlePlaceBet}
            >
              <Target className="h-4 w-4" />
              {isEditingExistingBet
                ? language === 'en'
                  ? 'Update bet'
                  : 'עדכן הימור'
                : t('submitBet') || (language === 'en' ? 'Submit bet' : 'שלח הימור')}
            </Button>
          </div>
        </Card>

        {otherUsersBets.length > 0 ? (
          <Card padding="p-4">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-brand-600" />
              <h3 className="font-semibold text-zinc-900">
                {language === 'en' ? 'Other predictions' : 'ניבויים של אחרים'}
              </h3>
              <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-bold text-brand-800">
                {otherUsersBets.length}
              </span>
            </div>
            <ul className="max-h-64 space-y-2 overflow-y-auto">
              {otherUsersBets.map((bet, index) => {
                const name = bet.user?.fullName || bet.user?.username || 'User';
                return (
                  <li
                    key={bet._id || index}
                    className="flex items-center justify-between gap-3 rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white">
                        {name.charAt(0).toUpperCase()}
                      </span>
                      <span className="truncate text-sm font-medium text-zinc-900">{name}</span>
                    </div>
                    <ScoreText
                      score={bet.predictedResult}
                      className="shrink-0 text-sm font-semibold text-brand-700"
                    />
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-zinc-500">
              <Eye className="h-3.5 w-3.5" />
              {language === 'en'
                ? `${otherUsersBets.length} other prediction(s) on this match`
                : `${otherUsersBets.length} ניבויים נוספים למשחק זה`}
            </p>
          </Card>
        ) : null}

        <Card className="border-brand-100 bg-brand-50/40" padding="p-4">
          <h3 className="mb-2 text-sm font-semibold text-brand-900">
            {language === 'en' ? 'Betting tips' : 'טיפים להימורים'}
          </h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-brand-900/90">
            <li>
              {language === 'en'
                ? 'Enter the predicted final score for both teams.'
                : 'הזן את התוצאה הסופית הצפויה לשתי הקבוצות.'}
            </li>
            <li>
              {language === 'en'
                ? 'You can bet only before kickoff.'
                : 'ניתן להמר רק לפני תחילת המשחק.'}
            </li>
            <li>
              {language === 'en'
                ? 'Points depend on prediction accuracy.'
                : 'הנקודות תלויות בדיוק הניבוי.'}
            </li>
          </ul>
        </Card>
      </div>
    </PageContainer>
  );
};

export default PlaceBet;
